import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Reveal, SectionHead } from "@/components/site/Reveal";
import { Badge } from "@/components/ui/badge";

export default function Blog() {
  useSeo({ title: "Blog", description: "Insights on web development, AI, design, SEO and digital growth from the Buddy Solution team." });
  const { data: posts = [] } = useQuery({ queryKey: ["blog"], queryFn: async () => (await api.get("/blog")).data });
  const [featured, ...rest] = posts;

  return (
    <div className="bs-container py-16 lg:py-20">
      <Reveal><SectionHead center eyebrow="Blog" title="Ideas, insights & know-how"
        subtitle="Practical writing on building, designing and growing digital products." /></Reveal>

      {featured && (
        <Reveal className="mt-12">
          <Link to={`/blog/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`}
            className="group grid gap-6 overflow-hidden bs-card md:grid-cols-2 hover:shadow-soft">
            <div className="overflow-hidden"><img src={featured.cover_image} alt={featured.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
            <div className="flex flex-col justify-center p-8">
              <Badge variant="secondary" className="w-fit">{featured.category}</Badge>
              <h2 className="mt-3 font-display text-2xl font-bold">{featured.title}</h2>
              <p className="mt-3 text-muted-foreground">{featured.excerpt}</p>
              <span className="mt-5 inline-flex items-center text-sm font-semibold text-primary">Read article <ArrowRight className="ml-1 h-4 w-4" /></span>
            </div>
          </Link>
        </Reveal>
      )}

      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {rest.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.05}>
            <Link to={`/blog/${p.slug}`} data-testid={`blog-item-${p.slug}`}
              className="group flex h-full flex-col overflow-hidden bs-card hover:-translate-y-1 hover:shadow-soft">
              <div className="overflow-hidden"><img src={p.cover_image} alt={p.title} className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /></div>
              <div className="flex flex-1 flex-col p-6">
                <Badge variant="secondary" className="w-fit">{p.category}</Badge>
                <h3 className="mt-3 font-display text-lg font-bold">{p.title}</h3>
                <p className="mt-2 flex-1 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
